// ===== Layer 3: 카테고리 유틸리티 (의존성: base.ts, categories.ts) =====

import type { CodeType } from 'src/types/base';
import { CATEGORIES, getCategoryByType, type CategoryDefinition } from 'src/configs/categories';

// ===== 매핑 유틸리티 =====

/**
 * 카테고리 제목 → CodeType 매핑
 */
export const createTitleToTypeMapping = (): Record<string, CodeType> => {
  const mapping: Record<string, CodeType> = {};
  
  CATEGORIES.forEach(category => {
    category.titles.forEach(title => {
      mapping[title] = category.type;
    });
  });
  
  return mapping;
};



/**
 * CodeType → 검색 필드들 매핑
 */
export const createSearchFieldsMapping = (): Record<CodeType, { fields: readonly string[] }> => {
  const mapping = {} as Record<CodeType, { fields: readonly string[] }>;
  
  CATEGORIES.forEach(category => {
    mapping[category.type] = { fields: category.searchFields };
  });
  
  return mapping;
};

// ===== 미리 생성된 매핑들 (성능 최적화) =====
export const TITLE_TO_TYPE_MAPPING = createTitleToTypeMapping();
export const SEARCH_FIELDS_MAPPING = createSearchFieldsMapping();

// ===== API/UI 설정 캐시 (성능 최적화) =====
const API_CONFIG_CACHE = new Map<CodeType, any>();
const UI_CONFIG_CACHE = new Map<CodeType, any>();

// ===== 직접 매핑 테이블 (O(1) 조회) =====
const CATEGORY_LOOKUP = new Map<CodeType, CategoryDefinition>();
CATEGORIES.forEach(category => {
  CATEGORY_LOOKUP.set(category.type, category);
});

// ===== 조회 함수들 =====

/**
 * 카테고리 제목으로 CodeType 찾기
 */
export const getCodeTypeFromTitle = (title: string): CodeType | undefined => {
  return TITLE_TO_TYPE_MAPPING[title];
};



/**
 * CodeType으로 검색 필드들 찾기
 */
export const getSearchFieldsByCodeType = (type: CodeType): readonly string[] => {
  return SEARCH_FIELDS_MAPPING[type]?.fields || [];
};

/**
 * CodeType으로 검색 함수 생성
 */
export const createSearchFunctionFromCategory = (categoryType: CodeType) => {
  const apiConfig = getApiConfig(categoryType);
  if (!apiConfig) return null;
  const primaryCodeField = Array.isArray(apiConfig.codeField)
    ? apiConfig.codeField[0]
    : apiConfig.codeField;
  
  return async (code: string, onFound: (item: any) => void) => {
      try {
          const response = await apiConfig.apiFunction(code);
          const found = response.data.data.find(
              (item: any) => item[primaryCodeField] === code
          );
          if (found) {
              onFound(found);
              return found;
          }
          return null;
      } catch (error) {
          console.error(`${categoryType} 검색 중 오류:`, error);
          return null;
      }
  };
};

/**
 * API 설정 조회 (캐시 적용)
 */
export const getApiConfig = (type: CodeType) => {
  // 캐시에서 먼저 확인
  if (API_CONFIG_CACHE.has(type)) {
    return API_CONFIG_CACHE.get(type);
  }
  
  const category = getCategoryByType(type);
  if (!category) return null;
  
  const config = {
    apiFunction: category.apiFunction,
    codeField: category.codeField,
    nameField: category.nameField,
    getApiParams: category.getApiParams,
    transformResponse: category.transformResponse
  };
  
  // 캐시에 저장
  API_CONFIG_CACHE.set(type, config);
  return config;
};

/**
 * UI 설정 조회 (캐시 적용)
 * @param type - 카테고리 타입
 * @param commonCodeStore - 공통코드 스토어 (동적 컬럼 생성을 위해 필요)
 */
export const getUiConfig = (type: CodeType, commonCodeStore?: any) => {
  const category = getCategoryByType(type);
  if (!category) return null;
  
  // columns가 함수인 경우 동적으로 생성 (캐시 불가)
  // columns가 배열인 경우에만 캐시 사용
  const isDynamicColumns = typeof category.columns === 'function';
  
  if (!isDynamicColumns && UI_CONFIG_CACHE.has(type)) {
    return UI_CONFIG_CACHE.get(type);
  }
  
  const columns = isDynamicColumns 
    ? (category.columns as (commonCodeStore?: any) => any[])(commonCodeStore)
    : category.columns as readonly any[];
  
  const config = {
    titles: category.titles,
    searchFields: category.searchFields,
    columns: columns,
    searchOptions: category.searchOptions,
    emptyMessage: category.emptyMessage,
  };
  
  // 정적 컬럼만 캐시에 저장
  if (!isDynamicColumns) {
    UI_CONFIG_CACHE.set(type, config);
  }
  
  return config;
};


